import React from 'react'
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { TrashIcon } from '../../icons'
import ValidationErrorText from '../common/ValidationErrorText'
import { getCurrencySymbol } from '../../utils/helperFunction'

const currencyOptions = [
  { code: 'INR', label: 'INR – Indian Rupee (₹)' },
  { code: 'USD', label: 'USD – US Dollar ($)' },
  { code: 'EUR', label: 'EUR – Euro (€)' },
  { code: 'GBP', label: 'GBP – British Pound Sterling (£)' },
  { code: 'AUD', label: 'AUD – Australian Dollar (A$)' },
  { code: 'CAD', label: 'CAD – Canadian Dollar (C$)' },
  { code: 'AED', label: 'AED – UAE Dirham (د.إ)' },
  { code: 'SGD', label: 'SGD – Singapore Dollar (S$)' },
  { code: 'MYR', label: 'MYR – Malaysian Ringgit (RM)' },
  { code: 'BDT', label: 'BDT – Bangladeshi Taka (৳)' },
]

// Default value structure for controlled mode
const defaultValue = {
  totalAmount: '',
  currency: 'INR',
  fees: [{ amount: '', currency: 'INR' }],
}

const Fees = ({
  value = defaultValue,
  onChange,
  readOnly = false,
  fieldError,
  fieldErrors,
  fieldType
}) => {
  // Defensive structure: ensure all keys exist for controlled usage
  const {
    totalAmount = '',
    currency = 'INR',
    fees = [{ amount: '', currency: 'INR' }],
  } = value || {}

  // Controlled field change helpers
  const handleFieldChange = (field, newVal) => {
    onChange && onChange({ ...value, [field]: newVal })
  }

const handleAmountChange = (index, newAmount, type) => {
  const rawValue = newAmount?.replace(/,/g, "");
  if (/^\d*\.?\d{0,2}$/.test(rawValue)) {
    const updatedFees =
      type === "fees"
        ? fees.map((item, i) =>
            i === index ? { ...item, amount: rawValue } : item
          )
        : rawValue;

    handleFieldChange(type, updatedFees);
  }
};


  const handleSemesterCurrencyChange = (index, newCurrency) => {
    const updatedFees = fees.map((item, i) =>
      i === index ? { ...item, currency: newCurrency } : item
    )
    handleFieldChange('fees', updatedFees)
  }

  const handleAdd = () => {
    handleFieldChange('fees', [...fees, { amount: '', currency }])
  }

  const handleRemove = (index) => {
    handleFieldChange('fees', fees.filter((_, i) => i !== index))
  }

  // Only local: for focus ring on main input
  const [isFocused, setIsFocused] = React.useState(false)

const formattedValue = (value) => {
  if (!value) return "";
  const [intPart, decPart] = value.split(".");
  const formattedInt = parseInt(intPart, 10).toLocaleString("en-IN");
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
};


  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Total Fee Row */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 250,
              maxWidth: '250px',
              border: '1px solid',
              borderColor: isFocused ? 'primary.main' : '#ccc',
              borderRadius: 1,
              overflow: 'hidden',
              transition: 'border-color 0.2s ease-in-out',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                backgroundColor: '#fff',
                fontSize: '14px',
                minWidth: 32,
                textAlign: 'center',
                borderRight: 'none',
              }}
            >
               {getCurrencySymbol(currency)}
            </Box>
            <TextField
              variant="standard"
              InputProps={{ disableUnderline: true, readOnly }}
              size="small"
              placeholder="00"
              value={formattedValue(totalAmount)}
              onChange={e => handleAmountChange('', e.target.value,"totalAmount")}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              sx={{
                minWidth: 100,
                '& input': { px: 1, fontSize: 13, paddingBottom: '0' },
              }}
              inputProps={{ style: { fontSize: 13 } }}
              disabled={readOnly}
            />
            <Select
              variant="standard"
              disableUnderline
              value={currency}
              onChange={e => handleFieldChange('currency', e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              renderValue={(selected) => selected}
              sx={{
                minWidth: 80,
                fontSize: '13px',
                '& .MuiSelect-select': {
                  px: 1,
                  paddingBottom: 0,
                },
              }}
              disabled={readOnly}
            >
              {currencyOptions.map((option) => (
                <MenuItem key={option.code} value={option.code} sx={{ fontSize: '13px' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <ValidationErrorText error={fieldErrors?.[`${fieldType}.fees.totalAmount`]}/>
          
        </Box>

        {fees.map((fee, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: '500' }}>
                Semester {index + 1}
              </Typography>
              <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  overflow: 'hidden',
                  '&:focus-within': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    minWidth: 32,
                    textAlign: 'center',
                    borderRight: 'none',
                  }}
                >
                  {getCurrencySymbol(fee?.currency)}
                </Box>
                <TextField
                  variant="standard"
                  InputProps={{ disableUnderline: true, readOnly }}
                  size="small"
                  placeholder="00"
                  value={formattedValue(fee.amount)}
                  onChange={e => handleAmountChange(index, e.target.value,"fees")}
                  sx={{
                    minWidth: 100,
                    '& input': { px: 1, fontSize: 13, paddingBottom: '0' },
                  }}
                  inputProps={{ style: { fontSize: 13 } }}
                  disabled={readOnly}
                />
                <Select
                  variant="standard"
                  disableUnderline
                  value={fee.currency}
                  onChange={e => handleSemesterCurrencyChange(index, e.target.value)}
                  renderValue={selected => selected}
                  sx={{
                    minWidth: 80,
                    fontSize: '13px',
                    '& .MuiSelect-select': {
                      px: 1,
                      paddingBottom: 0,
                    },
                  }}
                  disabled={readOnly}
                >
                  {currencyOptions.map((option) => (
                    <MenuItem key={option.code} value={option.code} sx={{ fontSize: '13px' }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            <ValidationErrorText error={fieldErrors?.[`${fieldType}.fees.fees[${index}].amount`]}/>
              </Box>
            </Box>
            {!readOnly && (
              <IconButton onClick={() => handleRemove(index)}>
                <TrashIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}

        {/* Add More Button */}
        {!readOnly && (
          <Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ color: '#1075FF', border: '1px solid #1075FF', textTransform: 'capitalize', px: 3, mt: 1 }}
            >
              Add More
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default Fees
